import { useEffect, useState } from 'react'

/** A destructive action that asks for confirmation inside the page, never via window.confirm().
 *
 *  Firefox offers "Prevent this page from creating additional dialogs" once a page has put up a
 *  couple of them, and Chrome has the same control. Tick it and EVERY later confirm() returns false
 *  immediately, with no dialog and no warning — so a delete button silently does nothing at all:
 *  no request, no error message, nothing in the audit log. The page looks broken in a way that
 *  leaves no trace anywhere to find, because from the app's side the user simply answered "no".
 *
 *  That is exactly how deleting opponent folders one by one came to look like it had got stuck
 *  halfway. An in-page confirmation cannot be suppressed by the browser, so the answer always
 *  reflects what the user actually chose. */
export function ConfirmButton({
  label,
  question,
  confirmLabel = 'yes, delete',
  onConfirm,
  disabled,
  className,
  title,
  timeoutMs = 10_000,
}: {
  label: string
  /** Shown in place of the button once armed — say what is about to be destroyed. */
  question: string
  confirmLabel?: string
  onConfirm: () => void
  disabled?: boolean
  className?: string
  title?: string
  timeoutMs?: number
}) {
  const [armed, setArmed] = useState(false)

  // Disarm itself, so a half-pressed delete isn't left sitting under the cursor to be finished off
  // by an unrelated click later.
  useEffect(() => {
    if (!armed) return
    const t = setTimeout(() => setArmed(false), timeoutMs)
    return () => clearTimeout(t)
  }, [armed, timeoutMs])

  if (!armed) {
    return (
      <button onClick={() => setArmed(true)} disabled={disabled} className={className} title={title}>
        {label}
      </button>
    )
  }
  return (
    <span className="inline-flex items-center gap-2 whitespace-nowrap text-[11px]">
      <span className="text-loss">{question}</span>
      <button
        onClick={() => {
          setArmed(false)
          onConfirm()
        }}
        className="text-loss underline underline-offset-2 hover:opacity-80"
      >
        {confirmLabel}
      </button>
      <button onClick={() => setArmed(false)} className="text-text-faint hover:text-text">
        cancel
      </button>
    </span>
  )
}
